import { atom, atomFamily, selector, selectorFamily } from 'recoil';
import {
	TRateCardSettings,
	TRateCardRules,
	TRateCardFuelLevies,
	TSettingsRateCardType,
	ILane,
	RATE_CARD_TRUCK_SIZE,
	TRateCardModifierSet,
	RateCardType,
	ILaneRateCard,
	TLinkedRateCard,
	TItemType,
} from '@types';
import { ZodFormattedError } from 'zod';
import { matchesRateCard, modifierUnsetCheck, TLaneSettingsSchema } from '@utils';
import { TLaneSchema } from '@utils/buildLaneSchema';

function setModifier<K extends keyof TRateCardModifierSet> (
	acc: TRateCardModifierSet,
	key: K,
	modifier: TRateCardModifierSet[K],
	attractsFuelLevy: boolean
) {
	acc[key] = { ...modifier, attracts_fuel_levy: attractsFuelLevy } as TRateCardModifierSet[K];
}

const keys = {
	RC_SETTINGS: '@Ofload/RateCardSettings',
	RC_SET_MODIFIERS: '@Ofload/RateCardSettingsModifiers',
	RC_SET_FULL_LOAD_TRUCKS: '@Ofload/RateCardSettingsFullLoadTrucks',
	RC_SET_FUEL_LEVIES: '@Ofload/RateCardSettingsFuelLevies',
	RC_SET_TYPES: '@Ofload/RateCardSettingsTypes',
	RC_RATE_CARD: '@Ofload/RateCard',
	RC_MODIFIERS: '@Ofload/RateCardModifiers',
	RC_RULES: '@Ofload/RateCardRules',
	RC_FUEL_LEVIES: '@Ofload/RateCardFuelLevies',
	RC_TYPES: '@Ofload/RateCardTypes',
	RC_FORM_ERRORS: '@Ofload/RateCardFormErrors',
	RC_ACTIVE_TYPE: '@Ofload/RateCardActiveType',
	RC_GLOBAL_SETTINGS: '@Ofload/RateCardGlobalSettings',
	RC_ACTIVE_ITEM_TYPE_ID: '@Ofload/RateCardActiveItemTypeId',
	RC_ALL_SETTING_MODIFIERS: '@Ofload/AllRateCardSettingModifiers',
	RC_ALL_RATE_CARD_MODIFIERS: '@Ofload/AllRateCardModifiers',
	RC_GLOBAL_RATE_CARD_ID: '@Ofload/GlobalRateCardId',

	RC_RATE_CARD_FAMILY: '@Ofload/RateCardFamily',
	RC_RATE_CARD_FUEL_LEVIES_FAMILY: '@Ofload/RateCardFuelLeviesFamily',
	RC_RATE_CARD_TYPES_FAMILY: '@Ofload/RateCardTypesFamily',
	RC_RATE_CARD_RULES_FAMILY: '@Ofload/RateCardRulesFamily',
	RC_RATE_CARD_MODIFIERS_FAMILY: '@Ofload/RateCardModifiersFamily',
	RC_ALL_RATE_CARD_FUEL_LEVY_MODIFIERS_FAMILY: '@Ofload/AllRateCardFuelLevyModifiersFamily',

	RC_LINKED_RATE_CARD: '@Ofload/LinkedRateCard',
	RC_LINKED_RATE_CARD_PAYLOAD: '@Ofload/LinkedRateCardPayload',

	ITEM_TYPES: '@Ofload/ItemTypes',
}


export const GlobalRateCardId = atom<string>({
	key: keys.RC_GLOBAL_RATE_CARD_ID,
	default: 'default'
});

/** The Customer's live Item types, sorted by name, as the API last listed them. */
export const ItemTypes = atom<TItemType[]>({
	key: keys.ITEM_TYPES,
	default: []
});

export const LinkedRateCard = atom<TLinkedRateCard | null>({
	key: keys.RC_LINKED_RATE_CARD,
	default: {
		linked_lane_external_id: '',
		carrier: { external_id: '', name: '' },
		shipper: { external_id: '', name: '' },
		margin_percentage: '',
		carrier_lane_id: '',
		shipper_lane_id: '',
	}
})

/* 	RATE CARD ==> */

// Atom family for managing multiple rate card instances
export const RateCardFamily = atomFamily<ILane | null, string>({
	key: keys.RC_RATE_CARD_FAMILY,
	default: null
});

/* Default rate card atom - backward compatible for existing components
   This now points to the 'default' instance of the family */
export const RateCard = atom<ILane | null>({
	key: keys.RC_RATE_CARD,
	default: null
});


export const RateCardFuelLevies = selector<TRateCardFuelLevies | null>({
	key: keys.RC_FUEL_LEVIES,
	get: ({ get }) => {
		const rateCard = get(RateCard);
		if (rateCard) return rateCard?.fuel_levies;
		return null;
	},
	set: ({ get, set }, newVal) => {
		const rateCard = get(RateCard);
		if (rateCard && newVal) {
			const update = {
				...rateCard,
				fuel_levies: newVal as TRateCardFuelLevies
			}
			set(RateCard, update);
		}
	}
});
/* 	RATE CARD SETTING ==> */

export const RateCardGlobalSettings = atom<TRateCardSettings | null>({
	key: keys.RC_GLOBAL_SETTINGS,
	default: null
});

export const RateCardSettings = atom<TRateCardSettings | null>({
	key: keys.RC_SETTINGS,
	default: null
});

export const RateCardActiveType = atom<RateCardType | null>({
	key: keys.RC_ACTIVE_TYPE,
	default: 'FULL_LOAD'
});

export const RateCardActiveItemTypeId = atom<string | null>({
	key: keys.RC_ACTIVE_ITEM_TYPE_ID,
	default: null
});

export const RateCardSettingsFullLoadTrucks = selector<RATE_CARD_TRUCK_SIZE[]>({
	key: keys.RC_SET_FULL_LOAD_TRUCKS,
	get: ({ get }) => {
		const settings = get(RateCardSettings);
		if (!settings) return [];
		return settings.settings.full_load
	},
	set: ({ get, set }, newVal) => {
		const settings = get(RateCardSettings);
		if (settings && newVal) {
			const newSettings = {
				...settings,
				settings: {
					...settings.settings,
					full_load: newVal as RATE_CARD_TRUCK_SIZE[]
				}
			}
			set(RateCardSettings, newSettings);
		}
	}
});

export const RateCardSettingsFuelLevies = selector<TRateCardFuelLevies | null>({
	key: keys.RC_SET_FUEL_LEVIES,
	get: ({ get }) => {
		const settings = get(RateCardSettings);
		if (!settings) return null;
		return settings.fuel_levies;
	},
	set: ({ get, set }, newVal) => {
		const settings = get(RateCardSettings);
		if (settings && newVal) {
			const newSettings = {
				...settings,
				fuel_levies: newVal as TRateCardFuelLevies
			}
			set(RateCardSettings, newSettings);
		}
	}
});

export const RateCardSettingsTypes = selector<TSettingsRateCardType[]>({
	key: keys.RC_SET_TYPES,
	get: ({ get }) => {
		const settings = get(RateCardSettings);
		if (!settings) return []
		const sortOrder = ['FULL_LOAD', 'PALLETS', 'WEIGHT', 'CPK', 'ITEM'];
		return [...settings.settings.rate_card_types].sort((a, b) => sortOrder.indexOf(a.type) - sortOrder.indexOf(b.type));
	},
	set: ({ get, set }, newVal) => {
		const settings = get(RateCardSettings);
		if (settings && newVal) {
			const newSettings = {
				...settings,
				settings: {
					...settings.settings,
					rate_card_types: newVal as TSettingsRateCardType[]
				}
			}
			set(RateCardSettings, newSettings);
		}
	}
});

export const RateCardTypes = selector<ILaneRateCard[]>({
	key: keys.RC_TYPES,
	get: ({ get }) => {
		const rateCard = get(RateCard);
		if (!rateCard) return []
		const sortOrder = ['FULL_LOAD', 'PALLETS', 'WEIGHT', 'CPK', 'ITEM'];
		return [...rateCard.rate_cards].sort((a, b) => sortOrder.indexOf(a.type) - sortOrder.indexOf(b.type));
	},
	set: ({ get, set }, newVal) => {
		const rateCard = get(RateCard);
		if (rateCard && newVal) {
			const update = {
				...rateCard,
				rate_cards: newVal as ILaneRateCard[]
			}
			set(RateCard, update);
		}
	}
});

/* 	Some state is mixed/used between both places exclusivly in the UI
		  In these cases a selector deriving the state from/to both parent
		  selectors/atoms is required */

export const RateCardRules = selector<{ rateCard?: TRateCardRules | null; settings?: TRateCardRules | null }>({
	key: keys.RC_RULES,
	get: ({ get }) => {
		const rateCard = get(RateCard);
		const rateCardSettings = get(RateCardSettings);
		const rateCardActiveType = get(RateCardActiveType);
		const activeItemTypeId = get(RateCardActiveItemTypeId);
		return {
			rateCard: rateCard?.rate_cards?.find((rc) => matchesRateCard(rc, rateCardActiveType, activeItemTypeId))?.rules || null,
			settings: rateCardSettings?.settings?.rate_card_types?.find((rc) => matchesRateCard(rc, rateCardActiveType, activeItemTypeId))?.rules || null
		}
	},
	set: ({ get, set }, newVal) => {
		const rateCard = get(RateCard);
		const rateCardSettings = get(RateCardSettings);
		const rateCardActiveType = get(RateCardActiveType);
		const activeItemTypeId = get(RateCardActiveItemTypeId);

		if ((newVal as Partial<{ rateCard: TRateCardRules | null; }>).rateCard) {
			const existingRateCardTypes = rateCard?.rate_cards?.map((rc) => {
				if (matchesRateCard(rc, rateCardActiveType, activeItemTypeId)) {
					return {
						...rc,
						rules: (newVal as Partial<{ rateCard: TRateCardRules | null; }>).rateCard
					}
				}
				return rc
			})

			const rcUpdate = {
				...rateCard,
				rate_cards: existingRateCardTypes
			} as ILane;
			set(RateCard, rcUpdate);
		}
		if (rateCardSettings && (newVal as Partial<{ settings: TRateCardRules }>).settings) {
			const existingRateCardTypes = rateCardSettings?.settings?.rate_card_types?.map((rc) => {
				if (matchesRateCard(rc, rateCardActiveType, activeItemTypeId)) {
					return {
						...rc,
						rules: (newVal as Partial<{ settings: TRateCardRules }>).settings as TRateCardRules
					}
				}
				return rc
			})

			const settingsUpdate = {
				...rateCardSettings,
				settings: {
					...rateCardSettings.settings,
					rate_card_types: existingRateCardTypes
				}
			}
			set(RateCardSettings, settingsUpdate);
		}
	}
});

export const RateCardModifiers = selector<{ rateCard?: TRateCardModifierSet | null; settings?: TRateCardModifierSet | null }>({
	key: keys.RC_MODIFIERS,
	get: ({ get }) => {
		const rateCard = get(RateCard);
		const rateCardSettings = get(RateCardSettings);
		const rateCardActiveType = get(RateCardActiveType);
		const activeItemTypeId = get(RateCardActiveItemTypeId);

		return {
			rateCard: rateCard?.rate_cards?.find((rc) => matchesRateCard(rc, rateCardActiveType, activeItemTypeId))?.modifier_set || null,
			settings: rateCardSettings?.settings?.rate_card_types?.find((rc) => matchesRateCard(rc, rateCardActiveType, activeItemTypeId))?.modifier_set || null
		}
	},
	set: ({ get, set }, newVal) => {
		const rateCard = get(RateCard);
		const rateCardSettings = get(RateCardSettings);
		const rateCardActiveType = get(RateCardActiveType);
		const activeItemTypeId = get(RateCardActiveItemTypeId);

		if ((newVal as Partial<{ rateCard: TRateCardModifierSet | null; }>).rateCard) {
			const existingRateCardTypes = rateCard?.rate_cards?.map((rc) => {
				if (matchesRateCard(rc, rateCardActiveType, activeItemTypeId)) {
					return {
						...rc,
						modifier_set: (newVal as Partial<{ rateCard: TRateCardModifierSet | null; }>).rateCard
					}
				}
				return rc
			})
			const rcUpdate = {
				...rateCard,
				rate_cards: existingRateCardTypes
			} as ILane;
			set(RateCard, rcUpdate);
		}
		if (rateCardSettings && (newVal as Partial<{ settings: TRateCardModifierSet }>).settings) {
			const existingRateCardTypes = rateCardSettings?.settings?.rate_card_types?.map((rc) => {
				if (matchesRateCard(rc, rateCardActiveType, activeItemTypeId)) {
					return {
						...rc,
						modifier_set: (newVal as Partial<{ settings: TRateCardModifierSet }>).settings as TRateCardModifierSet
					}
				}
				return rc
			})

			const settingsUpdate = {
				...rateCardSettings,
				settings: {
					...rateCardSettings?.settings,
					rate_card_types: existingRateCardTypes,
				}
			}
			set(RateCardSettings, settingsUpdate);
		}
	}
});

export const RateCardFormErrors = atom<ZodFormattedError<TLaneSettingsSchema | TLaneSchema> | null>({
	key: keys.RC_FORM_ERRORS,
	default: null
});

export const AllRateCardSettingFuelLevyModifiers = selector<TRateCardModifierSet | null>({
	key: keys.RC_ALL_SETTING_MODIFIERS,
	get: ({ get }) => {
		const rateCardSettings = get(RateCardSettings);

		const modifiers = rateCardSettings?.settings?.rate_card_types?.reduce((acc: TRateCardModifierSet, rc: TSettingsRateCardType) => {
			Object.entries(rc.modifier_set).forEach(([key, modifier]) => {
				if (!modifierUnsetCheck(modifier)) {
					Object.assign(acc, { [key]: modifier });
				}
			});
			return acc;
		}, {} as TRateCardModifierSet) || null;

		return modifiers
	},
	set: ({ get, set }, newVal) => {
		const settings = get(RateCardSettings);
		if (settings && newVal) {
			const newSettings = {
				...settings,
				settings: {
					...settings.settings,
					rate_card_types: settings.settings.rate_card_types.map((rc) => {
						const newModifierSet = Object.entries(rc.modifier_set).reduce((acc: TRateCardModifierSet, [key, modifier]) => {
							const typedKey = key as keyof TRateCardModifierSet;
							if (typeof (newVal as TRateCardModifierSet)[typedKey] !== 'undefined' && !modifierUnsetCheck(modifier)) {
								setModifier(acc, typedKey, modifier as TRateCardModifierSet[typeof typedKey], !!(newVal as TRateCardModifierSet)[typedKey]?.attracts_fuel_levy);

								return acc;
							}
							return Object.assign(acc, { [key]: modifier });
						}, {} as TRateCardModifierSet);

						return {
							...rc,
							modifier_set: newModifierSet
						}
					})
				}
			} as TRateCardSettings;
			set(RateCardSettings, newSettings);
		}
	}
});

export const AllRateCardFuelLevyModifiers = selector<TRateCardModifierSet | null>({
	key: keys.RC_ALL_RATE_CARD_MODIFIERS,
	get: ({ get }) => {
		const rateCard = get(RateCard);

		const modifiers = rateCard?.rate_cards?.reduce((acc, rc: ILaneRateCard) => {
			Object.entries(rc.modifier_set).forEach(([key, modifier]) => {
				if (!modifierUnsetCheck(modifier)) {
					Object.assign(acc, { [key]: modifier });
				}
			});
			return acc;
		}, {} as TRateCardModifierSet) || null;

		return modifiers
	},
	set: ({ get, set }, newVal) => {
		const rateCard = get(RateCard);
		if (rateCard && newVal) {
			const newRateCard = {
				...rateCard,
				rate_cards: rateCard.rate_cards.map((rc) => {
					const newModifierSet = Object.entries(rc.modifier_set).reduce((acc: TRateCardModifierSet, [key, modifier]) => {
						const typedKey = key as keyof TRateCardModifierSet;
						if (typeof (newVal as TRateCardModifierSet)[typedKey] !== 'undefined' && !modifierUnsetCheck(modifier)) {
							setModifier(acc, typedKey, modifier as TRateCardModifierSet[typeof typedKey], !!(newVal as TRateCardModifierSet)[typedKey]?.attracts_fuel_levy);

							return acc;
						}
						return Object.assign(acc, { [key]: modifier });
					}, {} as TRateCardModifierSet);

					return {
						...rc,
						modifier_set: newModifierSet
					}
				})
			} as ILane;
			set(RateCard, newRateCard);
		}
	}
});

/* ======================================================================
   ATOM FAMILY SELECTORS
   These selector families work with RateCardFamily for multi-instance support
   ====================================================================== */

export const LinkedRateCardPayloadSelector = selector<{
	carrier: ILane | null;
	shipper: ILane | null;
} | null>({
	key: keys.RC_LINKED_RATE_CARD_PAYLOAD,
	get: ({ get }) => {
		const carrierRateCard = get(RateCardFamily('carrier'));
		const shipperRateCard = get(RateCardFamily('shipper'));

		// only return a payload if both are populated
		if (!carrierRateCard || !shipperRateCard) return null;

		return {
			carrier: carrierRateCard,
			shipper: shipperRateCard,
		};
	}
});

export const RateCardFuelLeviesFamily = selectorFamily<TRateCardFuelLevies | null, string>({
	key: keys.RC_RATE_CARD_FUEL_LEVIES_FAMILY,
	get: (rateCardId: string) => ({ get }) => {
		const rateCard = get(RateCardFamily(rateCardId));
		if (rateCard) return rateCard?.fuel_levies;
		return null;
	},
	set: (rateCardId: string) => ({ get, set }, newVal) => {
		const rateCard = get(RateCardFamily(rateCardId));
		if (rateCard && newVal) {
			const update = {
				...rateCard,
				fuel_levies: newVal as TRateCardFuelLevies
			}
			set(RateCardFamily(rateCardId), update);
		}
	}
});

export const RateCardTypesFamily = selectorFamily<ILaneRateCard[], string>({
	key: keys.RC_RATE_CARD_TYPES_FAMILY,
	get: (rateCardId: string) => ({ get }) => {
		const rateCard = get(RateCardFamily(rateCardId));
		if (!rateCard) return []
		const sortOrder = ['FULL_LOAD', 'PALLETS', 'WEIGHT', 'CPK', 'ITEM'];
		return [...rateCard.rate_cards].sort((a, b) => sortOrder.indexOf(a.type) - sortOrder.indexOf(b.type));
	},
	set: (rateCardId: string) => ({ get, set }, newVal) => {
		const rateCard = get(RateCardFamily(rateCardId));
		if (rateCard && newVal) {
			const update = {
				...rateCard,
				rate_cards: newVal as ILaneRateCard[]
			}
			set(RateCardFamily(rateCardId), update);
		}
	}
});

export const RateCardRulesFamily = selectorFamily<{ rateCard?: TRateCardRules | null; settings?: TRateCardRules | null }, string>({
	key: keys.RC_RATE_CARD_RULES_FAMILY,
	get: (rateCardId: string) => ({ get }) => {
		const rateCard = get(RateCardFamily(rateCardId));
		const rateCardSettings = get(RateCardSettings);
		const rateCardActiveType = get(RateCardActiveType);
		const activeItemTypeId = get(RateCardActiveItemTypeId);
		return {
			rateCard: rateCard?.rate_cards?.find((rc) => matchesRateCard(rc, rateCardActiveType, activeItemTypeId))?.rules || null,
			settings: rateCardSettings?.settings?.rate_card_types?.find((rc) => matchesRateCard(rc, rateCardActiveType, activeItemTypeId))?.rules || null
		}
	},
	set: (rateCardId: string) => ({ get, set }, newVal) => {
		const rateCard = get(RateCardFamily(rateCardId));
		const rateCardSettings = get(RateCardSettings);
		const rateCardActiveType = get(RateCardActiveType);
		const activeItemTypeId = get(RateCardActiveItemTypeId);

		if ((newVal as Partial<{ rateCard: TRateCardRules | null; }>).rateCard) {
			const existingRateCardTypes = rateCard?.rate_cards?.map((rc) => {
				if (matchesRateCard(rc, rateCardActiveType, activeItemTypeId)) {
					return {
						...rc,
						rules: (newVal as Partial<{ rateCard: TRateCardRules | null; }>).rateCard
					}
				}
				return rc
			})

			const rcUpdate = {
				...rateCard,
				rate_cards: existingRateCardTypes
			} as ILane;
			set(RateCardFamily(rateCardId), rcUpdate);
		}
		if (rateCardSettings && (newVal as Partial<{ settings: TRateCardRules }>).settings) {
			const existingRateCardTypes = rateCardSettings?.settings?.rate_card_types?.map((rc) => {
				if (matchesRateCard(rc, rateCardActiveType, activeItemTypeId)) {
					return {
						...rc,
						rules: (newVal as Partial<{ settings: TRateCardRules }>).settings as TRateCardRules
					}
				}
				return rc
			})

			const settingsUpdate = {
				...rateCardSettings,
				settings: {
					...rateCardSettings.settings,
					rate_card_types: existingRateCardTypes
				}
			}
			set(RateCardSettings, settingsUpdate);
		}
	}
});

export const RateCardModifiersFamily = selectorFamily<{ rateCard?: TRateCardModifierSet | null; settings?: TRateCardModifierSet | null }, string>({
	key: keys.RC_RATE_CARD_MODIFIERS_FAMILY,
	get: (rateCardId: string) => ({ get }) => {
		const rateCard = get(RateCardFamily(rateCardId));
		const rateCardSettings = get(RateCardSettings);
		const rateCardActiveType = get(RateCardActiveType);
		const activeItemTypeId = get(RateCardActiveItemTypeId);

		return {
			rateCard: rateCard?.rate_cards?.find((rc) => matchesRateCard(rc, rateCardActiveType, activeItemTypeId))?.modifier_set || null,
			settings: rateCardSettings?.settings?.rate_card_types?.find((rc) => matchesRateCard(rc, rateCardActiveType, activeItemTypeId))?.modifier_set || null
		}
	},
	set: (rateCardId: string) => ({ get, set }, newVal) => {
		const rateCard = get(RateCardFamily(rateCardId));
		const rateCardSettings = get(RateCardSettings);
		const rateCardActiveType = get(RateCardActiveType);
		const activeItemTypeId = get(RateCardActiveItemTypeId);

		if ((newVal as Partial<{ rateCard: TRateCardModifierSet | null; }>).rateCard) {
			const existingRateCardTypes = rateCard?.rate_cards?.map((rc) => {
				if (matchesRateCard(rc, rateCardActiveType, activeItemTypeId)) {
					return {
						...rc,
						modifier_set: (newVal as Partial<{ rateCard: TRateCardModifierSet | null; }>).rateCard
					}
				}
				return rc
			})
			const rcUpdate = {
				...rateCard,
				rate_cards: existingRateCardTypes
			} as ILane;
			set(RateCardFamily(rateCardId), rcUpdate);
		}
		if (rateCardSettings && (newVal as Partial<{ settings: TRateCardModifierSet }>).settings) {
			const existingRateCardTypes = rateCardSettings?.settings?.rate_card_types?.map((rc) => {
				if (matchesRateCard(rc, rateCardActiveType, activeItemTypeId)) {
					return {
						...rc,
						modifier_set: (newVal as Partial<{ settings: TRateCardModifierSet }>).settings as TRateCardModifierSet
					}
				}
				return rc
			})

			const settingsUpdate = {
				...rateCardSettings,
				settings: {
					...rateCardSettings?.settings,
					rate_card_types: existingRateCardTypes,
				}
			}
			set(RateCardSettings, settingsUpdate);
		}
	}
});

export const AllRateCardFuelLevyModifiersFamily = selectorFamily<TRateCardModifierSet | null, string>({
	key: keys.RC_ALL_RATE_CARD_FUEL_LEVY_MODIFIERS_FAMILY,
	get: (rateCardId: string) => ({ get }) => {
		const rateCard = get(RateCardFamily(rateCardId));

		const modifiers = rateCard?.rate_cards?.reduce((acc, rc: ILaneRateCard) => {
			Object.entries(rc.modifier_set).forEach(([key, modifier]) => {
				if (!modifierUnsetCheck(modifier)) {
					Object.assign(acc, { [key]: modifier });
				}
			});
			return acc;
		}, {} as TRateCardModifierSet) || null;

		return modifiers
	},
	set: (rateCardId: string) => ({ get, set }, newVal) => {
		const rateCard = get(RateCardFamily(rateCardId));
		if (rateCard && newVal) {
			const newRateCard = {
				...rateCard,
				rate_cards: rateCard.rate_cards.map((rc) => {
					const newModifierSet = Object.entries(rc.modifier_set).reduce((acc: TRateCardModifierSet, [key, modifier]) => {
						const typedKey = key as keyof TRateCardModifierSet;
						if (typeof (newVal as TRateCardModifierSet)[typedKey] !== 'undefined' && !modifierUnsetCheck(modifier)) {
							setModifier(acc, typedKey, modifier as TRateCardModifierSet[typeof typedKey], !!(newVal as TRateCardModifierSet)[typedKey]?.attracts_fuel_levy);

							return acc;
						}
						return Object.assign(acc, { [key]: modifier });
					}, {} as TRateCardModifierSet);

					return {
						...rc,
						modifier_set: newModifierSet
					}
				})
			} as ILane;
			set(RateCardFamily(rateCardId), newRateCard);
		}
	}
});